import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  AppBar,
  Toolbar,
  CircularProgress,
} from "@mui/material";
import { Edit, Delete, Add, Logout, CloudUpload } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { articleService, Article } from "../services/articleService";
import { clearAdminSession } from "../utils/adminSecurity";
import { useTranslation } from "react-i18next";
import BlockEditor from "../components/BlockEditor";
import { ContentBlock } from "../types/editor";

const AdminDashboard: React.FC = () => {
  const [articlesList, setArticlesList] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<Article | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [dialogSuccessMessage, setDialogSuccessMessage] = useState("");
  const [dialogErrorMessage, setDialogErrorMessage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<{
    [key: string]: File | null;
  }>({});
  const navigate = useNavigate();
  const { t } = useTranslation();

  // File validation constants
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const ALLOWED_FILE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/gif",
  ];

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      const articles = await articleService.getArticles();
      // setArticlesList(articles);
      const processedArticles = articles.map((a: any) => {
        // Check if this is a Supabase article with direct content
        if (a.content && typeof a.content === "object" && a.content.en) {
          // Supabase article with bilingual content - preserve the content structure for editing
          const currentLang = t("language") || "en";
          const langContent = a.content[currentLang] || a.content.en;
          return {
            ...a,
            // Keep the original content structure for editing
            content: a.content,
            // Add flattened fields for display
            title: langContent.title || t(a.titleKey),
            meta: langContent.meta || t(a.metaKey),
            contentTitle: langContent.contentTitle || t(a.contentTitleKey),
            displayContent:
              langContent.content || t(a.contentKey, { returnObjects: true }),
            tags:
              langContent.tags ||
              (t(a.tagsKey, { returnObjects: true }) as string[] | undefined),
          };
        } else {
          // Hardcoded article using translation keys
          // For hardcoded articles, we need to create a bilingual structure for editing
          const enContent = {
            title: t(a.titleKey, { lng: "en" }),
            meta: t(a.metaKey, { lng: "en" }),
            contentTitle: t(a.contentTitleKey, { lng: "en" }),
            content: t(a.contentKey, { lng: "en", returnObjects: true }),
            tags: t(a.tagsKey, { lng: "en", returnObjects: true }) as
              | string[]
              | undefined,
          };

          const ruContent = {
            title: t(a.titleKey, { lng: "ru" }),
            meta: t(a.metaKey, { lng: "ru" }),
            contentTitle: t(a.contentTitleKey, { lng: "ru" }),
            content: t(a.contentKey, { lng: "ru", returnObjects: true }),
            tags: t(a.tagsKey, { lng: "ru", returnObjects: true }) as
              | string[]
              | undefined,
          };

          return {
            ...a,
            // Create bilingual content structure for editing
            content: {
              en: enContent,
              ru: ruContent,
            },
            // Keep display fields for current language
            title: t(a.titleKey),
            meta: t(a.metaKey),
            contentTitle: t(a.contentTitleKey),
            displayContent: t(a.contentKey, { returnObjects: true }),
            tags: t(a.tagsKey, { returnObjects: true }) as string[] | undefined,
          };
        }
      });
      setArticlesList(processedArticles);
    } catch (error) {
      setErrorMessage("Failed to load articles");
      console.error("Error loading articles:", error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadArticles();
    // Fix any article key conflicts on load
    articleService.fixArticleKeyConflicts();
  }, [loadArticles]);

  const handleLogout = () => {
    clearAdminSession();
    navigate("/admin");
  };

  const handleAddArticle = () => {
    setEditingArticle({
      slug: "",
      image: "",
      imageDetails: "",
      titleKey: "",
      metaKey: "",
      contentTitleKey: "",
      contentKey: "",
      tagsKey: "",
      content: {
        en: {
          title: "",
          meta: "",
          contentTitle: "",
          content: "",
          tags: "",
        },
        ru: {
          title: "",
          meta: "",
          contentTitle: "",
          content: "",
          tags: "",
        },
      },
    });
    setSelectedFiles({});
    setOpenDialog(true);
    setDialogSuccessMessage("");
    setDialogErrorMessage("");
  };

  const handleEditArticle = (article: Article) => {
    // Articles now have proper content structure from loadArticles
    setEditingArticle(article);
    setSelectedFiles({});
    setOpenDialog(true);
    setDialogSuccessMessage("");
    setDialogErrorMessage("");
  };

  const handleDeleteArticle = (article: Article) => {
    setArticleToDelete(article);
    setDeleteDialog(true);
  };

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is ${
        MAX_FILE_SIZE / (1024 * 1024)
      }MB`;
    }

    // Check file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return `Invalid file type. Allowed types: ${ALLOWED_FILE_TYPES.map(
        (type) => type.split("/")[1]
      ).join(", ")}`;
    }

    // Check file name length
    if (file.name.length > 100) {
      return "File name too long. Maximum 100 characters allowed.";
    }

    return null; // File is valid
  };

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: "image" | "imageDetails"
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file before storing
    const validationError = validateFile(file);
    if (validationError) {
      setDialogErrorMessage(validationError);
      setTimeout(() => setDialogErrorMessage(""), 4000);
      event.target.value = "";
      setSelectedFiles((prev) => ({ ...prev, [field]: null }));
      return;
    }

    // Store selected file and clear any errors
    setSelectedFiles((prev) => ({ ...prev, [field]: file }));
    setDialogErrorMessage("");
  };

  const handleImageUpload = async (field: "image" | "imageDetails") => {
    const file = selectedFiles[field];
    if (!file || !editingArticle) return;

    try {
      setUploadingImage(true);
      setDialogErrorMessage(""); // Clear any previous errors

      const fileName = `${Date.now()}-${file.name}`;
      const imageUrl = await articleService.uploadImage(file, fileName);

      if (imageUrl) {
        setEditingArticle((prev) =>
          prev
            ? {
                ...prev,
                [field]: imageUrl,
              }
            : null
        );
        setDialogSuccessMessage("Image uploaded successfully!");
        setTimeout(() => setDialogSuccessMessage(""), 3000);
        // Clear selected file after successful upload
        setSelectedFiles((prev) => ({ ...prev, [field]: null }));
      } else {
        setDialogErrorMessage("Failed to upload image");
      }
    } catch (error) {
      setDialogErrorMessage("Failed to upload image");
      console.error("Error uploading image:", error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveArticle = async () => {
    if (!editingArticle) return;

    if (editingArticle.slug.trim() === "") {
      setDialogErrorMessage("Slug is required");
      setTimeout(() => setDialogErrorMessage(""), 3000);
      return;
    }

    try {
      // Upload any selected files first
      let finalArticle = { ...editingArticle };

      // Upload main image if selected but not uploaded
      if (selectedFiles.image && !editingArticle.image) {
        setUploadingImage(true);
        const fileName = `${Date.now()}-${selectedFiles.image.name}`;
        const imageUrl = await articleService.uploadImage(
          selectedFiles.image,
          fileName
        );
        if (imageUrl) {
          finalArticle.image = imageUrl;
          setSelectedFiles((prev) => ({ ...prev, image: null }));
        } else {
          setDialogErrorMessage("Failed to upload main image");
          setUploadingImage(false);
          return;
        }
        setUploadingImage(false);
      }

      // Upload detail image if selected but not uploaded
      if (selectedFiles.imageDetails && !editingArticle.imageDetails) {
        setUploadingImage(true);
        const fileName = `${Date.now()}-${selectedFiles.imageDetails.name}`;
        const imageUrl = await articleService.uploadImage(
          selectedFiles.imageDetails,
          fileName
        );
        if (imageUrl) {
          finalArticle.imageDetails = imageUrl;
          setSelectedFiles((prev) => ({ ...prev, imageDetails: null }));
        } else {
          setDialogErrorMessage("Failed to upload detail image");
          setUploadingImage(false);
          return;
        }
        setUploadingImage(false);
      }

      // Handle image logic: if only one image is provided, use it for both
      if (finalArticle.image && !finalArticle.imageDetails) {
        // If only main image is provided, use it for detail image too
        finalArticle.imageDetails = finalArticle.image;
      } else if (finalArticle.imageDetails && !finalArticle.image) {
        // If only detail image is provided, use it for main image too
        finalArticle.image = finalArticle.imageDetails;
      }

      const isNew = !articlesList.find((a) => a.slug === finalArticle.slug);
      const success = isNew
        ? await articleService.addArticle(finalArticle)
        : await articleService.updateArticle(finalArticle);

      if (success) {
        await loadArticles();
        setOpenDialog(false);
        setEditingArticle(null);
        setSuccessMessage("Article saved successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setDialogErrorMessage("Failed to save article");
      }
    } catch (error) {
      setDialogErrorMessage("Failed to save article");
      console.error("Error saving article:", error);
      setUploadingImage(false);
    }
  };

  const confirmDelete = async () => {
    if (!articleToDelete) return;

    try {
      const success = await articleService.deleteArticle(articleToDelete.slug);
      if (success) {
        await loadArticles();
        setDeleteDialog(false);
        setArticleToDelete(null);
        setSuccessMessage("Article deleted successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setErrorMessage("Failed to delete article");
      }
    } catch (error) {
      setErrorMessage("Failed to delete article");
      console.error("Error deleting article:", error);
    }
  };

  const updateContentField = (
    lang: "en" | "ru",
    field: "title" | "meta" | "contentTitle" | "content" | "tags",
    value: string
  ) => {
    setEditingArticle((prev) => {
      if (!prev) return null;

      const currentContent = prev.content || {
        en: { title: "", meta: "", contentTitle: "", content: "", tags: "" },
        ru: { title: "", meta: "", contentTitle: "", content: "", tags: "" },
      };

      return {
        ...prev,
        content: {
          ...currentContent,
          [lang]: {
            ...currentContent[lang],
            [field]: value,
          },
        },
      };
    });
  };

  // Helper functions for block editor
  const parseContentToBlocks = (content: any): ContentBlock[] => {
    if (Array.isArray(content)) {
      return content;
    }
    if (typeof content === "string" && content.trim()) {
      return [{ type: "paragraph", text: content }];
    }
    if (content === null || content === undefined) {
      return [];
    }
    return [];
  };

  const convertBlocksToContent = (blocks: ContentBlock[]): any => {
    if (blocks.length === 0) return [];
    // Always return the blocks array to maintain consistency
    return blocks;
  };

  const handleContentBlocksChange = (
    lang: "en" | "ru",
    blocks: ContentBlock[]
  ) => {
    const content = convertBlocksToContent(blocks);
    updateContentField(lang, "content", content);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Admin Dashboard
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<Logout />}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h4" component="h1">
            Article Management
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* <Button
              variant="outlined"
              onClick={() => {
                articleService.resetToDefault();
                loadArticles();
                setSuccessMessage("Reset to default articles");
                setTimeout(() => setSuccessMessage(""), 3000);
              }}
            >
              Reset to Default
            </Button> */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddArticle}
            >
              Add New Article
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Slug</TableCell>
                <TableCell>Title (EN)</TableCell>
                <TableCell>Title (RU)</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {articlesList.map((article) => (
                <TableRow key={article.slug}>
                  <TableCell>{article.slug}</TableCell>
                  <TableCell>
                    {article.content?.en?.title || article.title}
                  </TableCell>
                  <TableCell>{article.content?.ru?.title || ""}</TableCell>
                  <TableCell>
                    {article.image && (
                      <img
                        src={article.image}
                        alt={article.slug}
                        style={{ width: 50, height: 50, objectFit: "cover" }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEditArticle(article)}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteArticle(article)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Edit/Add Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingArticle?.slug ? "Edit Article" : "Add New Article"}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              {dialogSuccessMessage && (
                <Alert severity="success">{dialogSuccessMessage}</Alert>
              )}
              {dialogErrorMessage && (
                <Alert severity="error">{dialogErrorMessage}</Alert>
              )}
              <TextField
                fullWidth
                label="Slug"
                value={editingArticle?.slug || ""}
                onChange={(e) =>
                  setEditingArticle((prev) =>
                    prev ? { ...prev, slug: e.target.value } : null
                  )
                }
              />

              {/* English Content */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                English Content
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Title (English)"
                  value={
                    editingArticle?.content?.en?.title ||
                    editingArticle?.title ||
                    ""
                  }
                  onChange={(e) =>
                    updateContentField("en", "title", e.target.value)
                  }
                />
                <TextField
                  fullWidth
                  label="Meta (English)"
                  value={
                    editingArticle?.content?.en?.meta ||
                    editingArticle?.meta ||
                    ""
                  }
                  onChange={(e) =>
                    updateContentField("en", "meta", e.target.value)
                  }
                />
              </Box>
              <TextField
                fullWidth
                label="Content Title (English)"
                value={
                  editingArticle?.content?.en?.contentTitle ||
                  editingArticle?.contentTitle ||
                  ""
                }
                onChange={(e) =>
                  updateContentField("en", "contentTitle", e.target.value)
                }
              />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Content (English)
              </Typography>
              <BlockEditor
                value={parseContentToBlocks(
                  editingArticle?.content?.en?.content ||
                    editingArticle?.content ||
                    ""
                )}
                onChange={(blocks) => handleContentBlocksChange("en", blocks)}
                language="en"
              />
              <TextField
                fullWidth
                label="Tags (English) - comma separated"
                value={
                  editingArticle?.content?.en?.tags ||
                  editingArticle?.tags ||
                  ""
                }
                onChange={(e) =>
                  updateContentField("en", "tags", e.target.value)
                }
              />

              {/* Russian Content */}
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Russian Content
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Title (Russian)"
                  value={editingArticle?.content?.ru?.title || ""}
                  onChange={(e) =>
                    updateContentField("ru", "title", e.target.value)
                  }
                />
                <TextField
                  fullWidth
                  label="Meta (Russian)"
                  value={editingArticle?.content?.ru?.meta || ""}
                  onChange={(e) =>
                    updateContentField("ru", "meta", e.target.value)
                  }
                />
              </Box>
              <TextField
                fullWidth
                label="Content Title (Russian)"
                value={editingArticle?.content?.ru?.contentTitle || ""}
                onChange={(e) =>
                  updateContentField("ru", "contentTitle", e.target.value)
                }
              />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Content (Russian)
              </Typography>
              <BlockEditor
                value={parseContentToBlocks(
                  editingArticle?.content?.ru?.content || ""
                )}
                onChange={(blocks) => handleContentBlocksChange("ru", blocks)}
                language="ru"
              />
              <TextField
                fullWidth
                label="Tags (Russian) - comma separated"
                value={editingArticle?.content?.ru?.tags || ""}
                onChange={(e) =>
                  updateContentField("ru", "tags", e.target.value)
                }
              />

              {/* Image Upload Sections */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, mb: 1 }}
              >
                💡 Tip: If you provide only one image, it will be used for both
                main and detail views
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Main Image
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    disabled={uploadingImage}
                    fullWidth
                  >
                    {uploadingImage ? "Uploading..." : "Upload Image"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e, "image")}
                    />
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5, fontSize: "0.75rem" }}
                  >
                    Max size: 2MB • Formats: JPEG, PNG, WebP, GIF
                  </Typography>

                  {/* File selection info */}
                  {selectedFiles.image && (
                    <Box
                      sx={{ mt: 1, p: 1, bgcolor: "grey.100", borderRadius: 1 }}
                    >
                      <Typography variant="caption" display="block">
                        Selected: {selectedFiles.image.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Size: {(selectedFiles.image.size / 1024).toFixed(1)}KB
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleImageUpload("image")}
                        disabled={uploadingImage}
                        sx={{ mt: 0.5 }}
                      >
                        {uploadingImage ? "Uploading..." : "Upload"}
                      </Button>
                    </Box>
                  )}

                  {editingArticle?.image && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" display="block">
                        Current: {editingArticle.image}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <img
                          src={editingArticle.image}
                          alt="Main"
                          style={{
                            width: 120,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Detail Image
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    disabled={uploadingImage}
                    fullWidth
                  >
                    {uploadingImage ? "Uploading..." : "Upload Image"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e, "imageDetails")}
                    />
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5, fontSize: "0.75rem" }}
                  >
                    Max size: 2MB • Formats: JPEG, PNG, WebP, GIF
                  </Typography>

                  {/* File selection info */}
                  {selectedFiles.imageDetails && (
                    <Box
                      sx={{ mt: 1, p: 1, bgcolor: "grey.100", borderRadius: 1 }}
                    >
                      <Typography variant="caption" display="block">
                        Selected: {selectedFiles.imageDetails.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Size:{" "}
                        {(selectedFiles.imageDetails.size / 1024).toFixed(1)}KB
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleImageUpload("imageDetails")}
                        disabled={uploadingImage}
                        sx={{ mt: 0.5 }}
                      >
                        {uploadingImage ? "Uploading..." : "Upload"}
                      </Button>
                    </Box>
                  )}

                  {editingArticle?.imageDetails && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" display="block">
                        Current: {editingArticle.imageDetails}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <img
                          src={editingArticle.imageDetails}
                          alt="Detail"
                          style={{
                            width: 120,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveArticle}
              variant="contained"
              disabled={uploadingImage}
            >
              {uploadingImage ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the article "
              {articleToDelete?.slug}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminDashboard;
